import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';

import { hasRoles, EDIT_USER_SITES_ROLES } from '../../../../util/RoleUtil';

import { assignSitesToUser, viewSitesForUser } from '../../../../services/StaffMemberService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { TableSearch } from '../../../common/table/table-search/TableSearch';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './UsersView.css';

export const UserViewSites = ({ context, user, navigateBack, setSpinnerText, showToaster }) => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [userFacilities, setUserFacilities] = useState([]);
  const [selectedUserFacilities, setSelectedUserFacilities] = useState([]);
  const [filteredUserFacilities, setFilteredUserFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [searchString] = useState('');

  const fetchSites = useCallback(async () => {
    setSpinnerText('Retrieving Sites');
    const [data, error] = await viewSitesForUser(context.username, user.externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedData = data.availableSite.map((item) => {
          return {
            facilityExternalReference: item.externalReference,
            facility: item.site,
            selected: false,
          };
        });
        setFacilities(formattedData);

        const formattedAssignedData = data.assignedSites.map((item) => {
          return {
            facilityExternalReference: item.externalReference,
            facility: item.site,
            selected: false,
          };
        });
        setUserFacilities(formattedAssignedData);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error?.message,
      });
    }
    setSpinnerText(null);
  }, [setSpinnerText, showToaster, context.username, user]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    setFilteredUserFacilities(
      userFacilities.filter((facility) => facility.facility.toLowerCase().includes(searchString.toLowerCase()))
    );

    setFilteredFacilities(
      facilities.filter((facility) => facility.facility.toLowerCase().includes(searchString.toLowerCase()))
    );
  }, [userFacilities, searchString, facilities]);

  const handleBackClick = () => {
    navigateBack();
  };

  const handleSaveChangesClick = async () => {
    setSpinnerText('Assigning Sites');

    const assignSitesToUserRequest = {
      sites: userFacilities.map((mapFacility) => ({
        externalReference: mapFacility.facilityExternalReference || '',
        site: mapFacility.facility || '',
      })),
      userExternalReference: user.externalReference || '',
    };

    const [data, error] = await assignSitesToUser(context.username, assignSitesToUserRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Successfully assigned sites to user.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error?.message,
      });
    }
    setSpinnerText(null);
  };

  const fieldsDisabled = !hasRoles(context, EDIT_USER_SITES_ROLES);

  const handleFacilityRowClick = (facility) => {
    if (fieldsDisabled) return;

    const isSelected = selectedFacilities.some(
      (selectedFacility) => selectedFacility.facilityExternalReference === facility.facilityExternalReference
    );
    setSelectedFacilities((prevSelectedFacilities) => {
      if (isSelected) {
        return prevSelectedFacilities.filter(
          (selectedFacility) => selectedFacility.facilityExternalReference !== facility.facilityExternalReference
        );
      } else {
        return [...prevSelectedFacilities, facility];
      }
    });
  };

  const handleUserFacilityRowClick = (facility) => {
    if (fieldsDisabled) return;

    const isSelected = selectedUserFacilities.some(
      (selectedFacility) => selectedFacility.facilityExternalReference === facility.facilityExternalReference
    );
    setSelectedUserFacilities((prevSelectedFacilities) => {
      if (isSelected) {
        return prevSelectedFacilities.filter(
          (selectedFacility) => selectedFacility.facilityExternalReference !== facility.facilityExternalReference
        );
      } else {
        return [...prevSelectedFacilities, facility];
      }
    });
  };

  const handleUnassignFacilityFromUser = () => {
    const uniqueSelectedFacilities = selectedUserFacilities.filter(
      (facility, index, self) =>
        index === self.findIndex((f) => f.facilityExternalReference === facility.facilityExternalReference)
    );

    setFacilities((prevFacilities) => {
      const newFacilities = uniqueSelectedFacilities.filter(
        (selectedFacility) =>
          !prevFacilities.some(
            (facility) => facility.facilityExternalReference === selectedFacility.facilityExternalReference
          )
      );

      return [
        ...prevFacilities,
        ...newFacilities.map((selectedFacility) => ({
          facilityExternalReference: selectedFacility.facilityExternalReference,
          facility: selectedFacility.facility,
        })),
      ];
    });

    setUserFacilities((prevUserFacilities) =>
      prevUserFacilities.filter(
        (userFacility) =>
          !uniqueSelectedFacilities.some(
            (selectedFacility) => selectedFacility.facilityExternalReference === userFacility.facilityExternalReference
          )
      )
    );

    setSelectedUserFacilities([]);
  };

  const handleUnassignFacilitiesFromUser = () => {
    setFacilities((prevFacilities) => [
      ...prevFacilities,
      ...userFacilities.map((userFacility) => ({
        facilityExternalReference: userFacility.facilityExternalReference,
        facility: userFacility.facility,
      })),
    ]);

    setUserFacilities([]);
    setSelectedUserFacilities([]);
  };

  const handleAssignFacilityToUser = () => {
    setUserFacilities((prevUserFacilities) => [
      ...prevUserFacilities,
      ...selectedFacilities.map((selectedFacility) => ({
        facilityExternalReference: selectedFacility.facilityExternalReference,
        facility: selectedFacility.facility,
      })),
    ]);

    setFacilities((prevFacilities) =>
      prevFacilities.filter(
        (facility) =>
          !selectedFacilities.some(
            (selectedFacility) => selectedFacility.facilityExternalReference === facility.facilityExternalReference
          )
      )
    );

    setSelectedFacilities([]);
  };

  const handleAssignFacilitiesToUser = () => {
    setUserFacilities((prevUserFacilities) => [
      ...prevUserFacilities,
      ...facilities.map((facility) => ({
        facilityExternalReference: facility.facilityExternalReference,
        facility: facility.facility,
      })),
    ]);

    setFacilities([]);
    setSelectedFacilities([]);
  };

  return (
    <div className="tab-container-sites">
      <div className="form-section-site">
        <div className="admin-sites-sections">
          <h3 className="user-sites-header">User Assigned Facilities</h3>
          <TableSearch
            dataToFilter={userFacilities}
            setFilteredData={setFilteredUserFacilities}
            setCurrentPage={() => undefined}
            searchBarWidth={'100%'}
            inputWidth={'100%'}
            margin={0}
            searchIconAlignment={'8px'}
          />
          <table className="sites-table">
            <tbody className="sites-table-body">
              {filteredUserFacilities.map((facility, index) => (
                <tr
                  className="sites-table-body-row"
                  key={index}
                  onClick={() => {
                    handleUserFacilityRowClick(facility);
                  }}
                >
                  <td>{facility.facility}</td>
                  <td>
                    <Checkbox
                      color={'#000000'}
                      fillStyle={false}
                      checked={selectedUserFacilities.some(
                        (selectedFacility) =>
                          selectedFacility.facilityExternalReference === facility.facilityExternalReference
                      )}
                      disabled={fieldsDisabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="form-section-mid">
          <div className="form-section-mid-buttons">
            <SaveButton
              color="#78a480"
              textColor="#ffffff"
              title={<FontAwesomeIcon className="arrow-icons-small" color={'#ffffff'} icon={faAngleRight} />}
              onClick={handleUnassignFacilityFromUser}
              disabled={fieldsDisabled}
            />
            <SaveButton
              color="#78a480"
              textColor="#ffffff"
              title={<FontAwesomeIcon className="arrow-icons" color={'#ffffff'} icon={faAngleDoubleRight} />}
              onClick={handleUnassignFacilitiesFromUser}
              disabled={fieldsDisabled}
            />
          </div>
          <div className="form-section-mid-buttons">
            <SaveButton
              color="#78a480"
              textColor="#ffffff"
              title={<FontAwesomeIcon className="arrow-icons" color={'#ffffff'} icon={faAngleDoubleLeft} />}
              onClick={handleAssignFacilitiesToUser}
              disabled={fieldsDisabled}
            />
            <SaveButton
              color="#78a480"
              textColor="#ffffff"
              title={<FontAwesomeIcon className="arrow-icons-small" color={'#ffffff'} icon={faAngleLeft} />}
              onClick={handleAssignFacilityToUser}
              disabled={fieldsDisabled}
            />
          </div>
        </div>

        <div className="admin-sites-sections">
          <h3 className="user-sites-header">Available Facilities</h3>
          <TableSearch
            dataToFilter={facilities}
            setFilteredData={setFilteredFacilities}
            setCurrentPage={() => undefined}
            searchBarWidth={'100%'}
            inputWidth={'100%'}
            margin={0}
            searchIconAlignment={'8px'}
          />
          <table className="sites-table">
            <tbody className="sites-table-body">
              {filteredFacilities.map((facility, index) => (
                <tr
                  className="sites-table-body-row"
                  key={index}
                  onClick={() => {
                    handleFacilityRowClick(facility);
                  }}
                >
                  <td>{facility.facility}</td>
                  <td>
                    <Checkbox
                      color={'#000000'}
                      fillStyle={false}
                      checked={selectedFacilities.some(
                        (selectedFacility) =>
                          selectedFacility.facilityExternalReference === facility.facilityExternalReference
                      )}
                      disabled={fieldsDisabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="user-view-buttons">
        <SaveButton
          color={'#ffffff'}
          textColor={'#78a480'}
          border={'1px solid #077187'}
          title={'Back'}
          onClick={handleBackClick}
        />
        <SaveButton
          color={'#78a480'}
          title={'Save Changes'}
          onClick={handleSaveChangesClick}
          disabled={fieldsDisabled}
        />
      </div>
    </div>
  );
};
